import { useUserStore, type User } from "@/state/user_state";
import { CustomerAuthenticationApi, type CustomerAccountJwt, type OneTimeTokenJwt } from "breuer-supercharged-sdk";
import axios, { type AxiosInstance } from "axios";
import { AuthError, AuthErrorCode, parseAuthError, getAuthErrorMessage } from "./auth_errors";

const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry

export class AuthService {
    private static instance: AuthService;
    private authApi: CustomerAuthenticationApi;
    private axiosInstance: AxiosInstance;
    private refreshPromise: Promise<void> | null = null;

    private constructor() {
        this.axiosInstance = axios.create({
            baseURL: import.meta.env.VITE_API_URL,
            withCredentials: false,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
        });
        this.authApi = new CustomerAuthenticationApi(undefined, undefined, this.axiosInstance);
        this.initializeFromStorage();
    }

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    private initializeFromStorage(): void {
        const state = useUserStore.getState();
        if (state.credentials?.accessToken) {
            this.validateAndRestoreSession();
        } else {
            useUserStore.getState().setLoading(false);
        }
    }

    private async validateAndRestoreSession(): Promise<void> {
        const state = useUserStore.getState();
        const credentials = state.credentials;

        if (!credentials?.accessToken) {
            useUserStore.getState().setLoading(false);
            return;
        }

        try {
            if (this.isTokenExpiringSoon(credentials.accessToken)) {
                if (credentials.refreshToken) {
                    await this.performTokenRefresh(credentials.refreshToken);
                } else {
                    this.clearAuth();
                }
            } else {
                const user = this.decodeToken(credentials.accessToken);
                useUserStore.getState().setUser(user);
            }
        } catch {
            this.clearAuth();
        } finally {
            useUserStore.getState().setLoading(false);
        }
    }

    private decodeToken(token: string): User {
        try {
            const payload = token.split('.')[1];
            const decoded = JSON.parse(atob(payload));
            return decoded as User;
        } catch {
            throw new AuthError(
                AuthErrorCode.INVALID_TOKEN,
                getAuthErrorMessage(AuthErrorCode.INVALID_TOKEN)
            );
        }
    }

    private isTokenExpiringSoon(token: string): boolean {
        try {
            const decoded = this.decodeToken(token);
            if (!decoded.iat) return true;

            const payload = token.split('.')[1];
            const fullDecoded = JSON.parse(atob(payload));
            const exp = fullDecoded.exp;

            if (!exp) return false;

            const expiryTime = exp * 1000;
            const now = Date.now();
            return now >= expiryTime - TOKEN_EXPIRY_BUFFER_MS;
        } catch {
            return true;
        }
    }

    private isTokenExpired(token: string): boolean {
        try {
            const payload = token.split('.')[1];
            const decoded = JSON.parse(atob(payload));
            const exp = decoded.exp;

            if (!exp) return false;

            return Date.now() >= exp * 1000;
        } catch {
            return true;
        }
    }

    private async performTokenRefresh(refreshToken: string): Promise<void> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = (async () => {
            try {
                const response = await this.authApi.refreshToken({ refreshToken });
                const { accessToken, refreshToken: newRefreshToken } = response.data;

                const user = this.decodeToken(accessToken);
                useUserStore.getState().setCredentials({
                    accessToken,
                    refreshToken: newRefreshToken
                });
                useUserStore.getState().setUser(user);
            } catch (error) {
                this.clearAuth();
                throw new AuthError(
                    AuthErrorCode.TOKEN_REFRESH_FAILED,
                    getAuthErrorMessage(AuthErrorCode.TOKEN_REFRESH_FAILED),
                    undefined,
                    error
                );
            } finally {
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }

    private clearAuth(): void {
        useUserStore.getState().clearAuth();
    }

    public async signInWithSAML(): Promise<never> {
        throw new AuthError(
            AuthErrorCode.NOT_SUPPORTED,
            'SAML authentication is not supported in the customer portal'
        );
    }

    public async signOut(): Promise<void> {
        const state = useUserStore.getState();
        const user = state.user;

        try {
            if (user && 'email' in user && user.tokenType === 'account') {
                const accessToken = await this.getToken();
                if (accessToken) {
                    await this.authApi.logout(undefined, { headers: { Authorization: `Bearer ${accessToken}` } });
                }
            }
        } catch (error) {
            // Silently handle logout API errors - we still want to clear local state
            console.error('Logout API call failed:', error);
        } finally {
            this.clearAuth();
        }
    }

    public async getToken(): Promise<string | undefined> {
        const state = useUserStore.getState();
        const credentials = state.credentials;

        if (!credentials?.accessToken) {
            return undefined;
        }

        if (this.isTokenExpired(credentials.accessToken)) {
            if (credentials.refreshToken && !this.isTokenExpired(credentials.refreshToken)) {
                try {
                    await this.performTokenRefresh(credentials.refreshToken);
                    return useUserStore.getState().credentials?.accessToken;
                } catch {
                    return undefined;
                }
            }
            return undefined;
        }

        if (this.isTokenExpiringSoon(credentials.accessToken) && credentials.refreshToken) {
            this.performTokenRefresh(credentials.refreshToken).catch(() => { });
        }

        return credentials.accessToken;
    }

    public getUser(): User | null {
        return useUserStore.getState().user;
    }

    public async signInWithEmailAndPassword(email: string, password: string): Promise<void> {
        try {
            const response = await this.authApi.login({ email, password });
            const { accessToken, refreshToken } = response.data;

            const user = this.decodeToken(accessToken) as CustomerAccountJwt;
            console.log('Authenticated user:', user);
            useUserStore.getState().setCredentials({ accessToken, refreshToken });
            useUserStore.getState().setUser(user);
        } catch (error) {
            throw parseAuthError(error);
        }
    }

    public async sendPasswordResetEmail(_email: string): Promise<never> {
        void _email;
        throw new AuthError(
            AuthErrorCode.NOT_IMPLEMENTED,
            'Password reset is not implemented yet. Please contact support.'
        );
    }

    public async createUserWithEmailAndPassword(_email: string, _password: string): Promise<never> {
        void _email;
        void _password;
        throw new AuthError(
            AuthErrorCode.NOT_SUPPORTED,
            'User registration is not available. Customer accounts are created by administrators.'
        );
    }

    public async exchangeOneTimeToken(oneTimeToken: string): Promise<void> {
        try {
            const response = await this.authApi.exchangeOneTimeToken({ oneTimeToken });
            const accessToken = response.data;

            const user = this.decodeToken(accessToken) as OneTimeTokenJwt;
            useUserStore.getState().setCredentials({ accessToken });
            useUserStore.getState().setUser(user);
        } catch (error) {
            throw parseAuthError(error);
        }
    }

    public async verifyOneTimeToken(oneTimeToken: string): Promise<OneTimeTokenJwt> {
        try {
            const response = await this.authApi.verifyOneTimeToken({ oneTimeToken });
            return response.data;
        } catch (error) {
            throw parseAuthError(error);
        }
    }

    public async verifyAccessToken(accessToken: string): Promise<CustomerAccountJwt> {
        try {
            const response = await this.authApi.verifyAccessToken(`Bearer ${accessToken}`);
            return response.data;
        } catch (error) {
            throw parseAuthError(error);
        }
    }

    public isAuthenticated(): boolean {
        const state = useUserStore.getState();
        return !!state.credentials?.accessToken && !!state.user;
    }

    public isOneTimeTokenUser(): boolean {
        const user = this.getUser();
        return user?.tokenType === 'onetime-token';
    }

    public isAccountUser(): boolean {
        const user = this.getUser();
        return user?.tokenType === 'account';
    }
}

// Re-export error types for convenience
export { AuthError, AuthErrorCode, parseAuthError, getAuthErrorMessage, getErrorMessage } from "./auth_errors";
